
import {
    Menu, Accordion, AccordionDetails, AccordionSummary, OutlinedInput, InputLabel, MenuItem,
    FormControl, Select, Chip, Box, Button, IconButton, Autocomplete, TextField, Stack
} from "@mui/material";
import { AppContext } from "helpers/context";
import { useContext, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./filter-locations.module.scss";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { NumericFormat } from 'react-number-format';
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/zh";

interface FilterLocationsProps {
    anchorEl: any;
    handleClose: any;
    handleChange: any;
    handleApply: any;
    data: any;
    isActiveData: any;
}

function FilterLocations(props: Readonly<FilterLocationsProps>) {
    const [t] = useTranslation();
    const [context, dispatch] = useContext(AppContext);
    const open = Boolean(props.anchorEl);
    const [filterData, setFilterData] = useState<any>(undefined);
    const [errors, setErrors] = useState<any>({})

    const MAX_VALUE = 1000000000000000;
    const validateInteger = ({ name, value, }: {
        name: string;
        value: string;
    }) => {
        if (value.trim() === "") return undefined;
        const num = Number(value);
        if (isNaN(num)) return t("locations.add.isNaN", { key: t("locationSet.filterModal." + name) });
        if (num < 0) return t("locations.add.less0", { key: t("locationSet.filterModal." + name) });
        if (num > MAX_VALUE) return t("locations.add.less9Q", { key: t("locationSet.filterModal." + name) });
        return undefined;
    }

    const updateError = (key: string, msg?: string) => {
        setErrors((prev: any) => ({ ...prev, [key]: msg }));
    };

    const validateMinMaxRelation = (title: string, name: string, min: any, max: any) => {
        const numMin = Number(min);
        const numMax = Number(max);
        if (name === "min" && numMax) {
            if (!isNaN(numMax) && numMin && numMin > numMax) {
                return updateError(title + "min", t("locations.add.moreMax"));
            }
            updateError(title + "min");
            if (errors[title + "max"] === t("locations.add.lessMin")) {
                updateError(title + "max");
            }
        }

        if (name === "max" && numMin) {
            if (!isNaN(numMin) && numMax && numMax < numMin) {
                return updateError(title + "max", t("locations.add.lessMin"));
            }
            updateError(title + "max");
            if (errors[title + "min"] === t("locations.add.moreMax")) {
                updateError(title + "min");
            }
        }
    };

    const handleChange = (event: any, parentIndex: number, childrenIndex?: number) => {
        const { name, value } = event.target;
        const parent = filterData[parentIndex];
        if (childrenIndex !== undefined) {
            const child = parent.children[childrenIndex];

            // 如果需要校验数字
            if (child.type === "number") {
                child[name] = value;

                const validateMsg = validateInteger({ name, value });
                const errKey = child.title + name;

                updateError(errKey, validateMsg);
                // 如果基本校验不通过
                if (!validateMsg) {
                    // 校验 min-max 关系
                    validateMinMaxRelation(child.title, name, child.min, child.max);
                }
            } else if (child.type === "select") {
                // 不需要数值校验，普通赋值
                child.value = value;
            } else {
                child[name] = value;
            }
        } else {
            // 父级字段
            parent.value = value;
        }

        setFilterData([...filterData]);
    };

    const isPresent = (v: unknown) => {
        if (v === null || v === undefined) return false;
        if (typeof v === 'string') return v.trim() !== '';   // 空串算无值
        if (Array.isArray(v)) return v.length > 0;           // 空数组算无值
        return true; // 其他类型（number/boolean/object）默认有值；0/false 也视为有值（如需调整见下文）
    };

    const btnDisabled = useMemo(() => {
        const hasValue = filterData?.some((v: any) =>
            isPresent(v.value) ||
            v.children?.some((item: any) =>
                isPresent(item.value) || isPresent(item.min) || isPresent(item.max)
            )
        );
        return hasValue; // 有任何值 => true
    }, [filterData]);


    const sameValue = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

    const sameData = (a: any[], b: any[]) => {
        let is = a.every((item, i) => {
            const origin = b[i];

            // 顶层只比 value
            if (!sameValue(item.value, origin.value)) return false;

            const aChildren = item.children ?? [];
            const bChildren = origin.children ?? [];

            // children 数量不同直接 false
            if (aChildren.length !== bChildren.length) return false;

            // children 内比 value / min / max
            return aChildren.every((c: any, idx: number) =>
                sameValue(c.value, bChildren[idx].value) &&
                sameValue(c.min, bChildren[idx].min) &&
                sameValue(c.max, bChildren[idx].max)
            );

        });
        let errorsArr = Object.values(errors).filter(v => v);
        return is || errorsArr.length > 0
    }

    const handleReset = () => {
        if (props.isActiveData) {
            let newData = [...filterData];
            let index = props.isActiveData.index;
            let childIdx = props.isActiveData.childIdx;
            if (childIdx === undefined) {
                newData[index].value = "";
            } else {
                let newChild = newData[index].children[childIdx];
                if (newChild.value !== undefined) newChild.value = [];
                if (newChild.type === "number") {
                    if (newChild.min !== undefined) newChild.min = "";
                    if (newChild.max !== undefined) newChild.max = "";
                } else {
                    if (newChild.min !== undefined) newChild.min = null;
                    if (newChild.max !== undefined) newChild.max = null;
                }
            }
            props.handleApply([...newData]);
            props.handleClose();
        } else {
            const newData = filterData.map((item: any) => {
                const newItem: any = { ...item };
                // 仅当 value 存在时清空（undefined 不清空）
                if (newItem.value !== undefined) {
                    newItem.value = "";
                }
                // children
                if (Array.isArray(newItem.children)) {
                    newItem.children = newItem.children.map((child: any) => {
                        const newChild: any = { ...child };
                        if (newChild.value !== undefined) newChild.value = [];
                        if (newChild.type === "number") {
                            if (newChild.min !== undefined) newChild.min = "";
                            if (newChild.max !== undefined) newChild.max = "";
                        } else {
                            if (newChild.min !== undefined) newChild.min = null;
                            if (newChild.max !== undefined) newChild.max = null;
                        }
                        return newChild;
                    });
                }

                return newItem;
            });
            setErrors({})
            props.handleApply(JSON.parse(JSON.stringify(newData)))
        }
    }

    const childrenEl = (data: any, index: number, childIdx?: number) => {
        if (data.type === "number") {
            return data.show ?
                <div key={data.title} style={{ margin: "10px 0" }}>
                    <div style={{
                        margin: "15px 0 10px",
                        fontStyle: "normal",
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: "#000000de",
                        fontWeight: 500
                    }}>{t("locationSet.filterModal." + data.title)}</div>
                    <Stack spacing={1} direction="row">
                        <NumericFormat
                            customInput={TextField}
                            name="min"
                            thousandSeparator=","
                            decimalSeparator="."
                            allowNegative={false}
                            type="text"
                            inputMode="decimal"
                            label={t("locationSet.filterModal.min")}
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{
                                minWidth: "180px",
                                flex: 1
                            }}
                            value={data.min}
                            error={Boolean(errors[data.title + "min"])}
                            helperText={errors[data.title + "min"]}
                            onValueChange={(values) => handleChange({ target: { name: 'min', value: values.value } }, index, childIdx)} // ← 纯净数值
                        />
                        <NumericFormat
                            customInput={TextField}
                            name="max"
                            thousandSeparator=","
                            decimalSeparator="."
                            allowNegative={false}
                            type="text"
                            inputMode="decimal"
                            label={t("locationSet.filterModal.max")}
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{
                                minWidth: "180px",
                                flex: 1
                            }}
                            value={data.max}
                            error={Boolean(errors[data.title + "max"])}
                            helperText={errors[data.title + "max"]}
                            onValueChange={(values) => handleChange({ target: { name: 'max', value: values.value } }, index, childIdx)} // ← 纯净数值
                        />
                    </Stack>
                </div >
                :
                (<div
                    key={data.title}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: "38px",
                        marginTop: "10px"
                    }}
                >
                    <span style={{
                        fontStyle: "normal",
                        fontWeight: 500,
                        fontSize: "13px",
                        color: "#0009",
                    }}
                    >{t("locationSet.filterModal." + data.title)}</span>
                    <span
                        style={{
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "13px",
                            color: "#0009",
                        }}

                    >{t("locationSet.filterModal.notValue")}</span>
                </div>)
        } else if (data.type === "select") {
            return <Autocomplete
                key={data.title}
                multiple
                value={data.value || []}
                onChange={(event, value) => handleChange({ target: { name: data.title, value } }, index, childIdx)}
                options={data.options}
                filterSelectedOptions
                renderOption={(props: any, option: any) => (
                    <li {...props} key={option}>
                        {data.title === "occupancy" ? t("occupancyOptions." + option) : option}
                    </li>
                )}
                renderTags={(value: any, getTagProps: any) =>
                    value.map((option: string, index: number) => (
                        <Chip
                            label={data.title === "occupancy" ? t("occupancyOptions." + option) : option}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                renderInput={(params: any) => (
                    <TextField
                        {...params}
                        label={t("locationSet.filterModal." + data.title)}
                        color="success"
                    />
                )}
                noOptionsText=""
                sx={{
                    marginTop: "10px"
                }}
            />
        } else if (data.type === "time") {
            return data.show ?
                <div key={data.title} style={{ margin: "10px 0" }}>
                    <div style={{
                        margin: "15px 0 10px",
                        fontStyle: "normal",
                        fontSize: "13px",
                        lineHeight: "20px",
                        color: "#000000de",
                        fontWeight: 500
                    }}>{t("locationSet.filterModal." + data.title)}</div>
                    <Stack spacing={1} direction="row">
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={context.language === "cn" ? "zh" : "en"}
                        >
                            <DatePicker
                                label={t("locationSet.filterModal.startDate")}
                                format="YYYY-MM-DD"
                                onChange={(val) => handleChange(
                                    { target: { name: 'min', value: val } },
                                    index,
                                    childIdx
                                )}
                                value={data.min}
                                maxDate={data.max}
                                slotProps={{
                                    textField: {
                                        color: "success",
                                        sx: {
                                            minWidth: "180px",
                                            flex: 1
                                        }
                                    },
                                }}
                            />
                            <DatePicker
                                label={t("locationSet.filterModal.endDate")}
                                format="YYYY-MM-DD"
                                onChange={(val) => handleChange(
                                    { target: { name: 'max', value: val } },
                                    index,
                                    childIdx
                                )}
                                value={data.max}
                                minDate={data.min}
                                slotProps={{
                                    textField: {
                                        color: "success",
                                        sx: {
                                            minWidth: "180px",
                                            flex: 1
                                        }
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Stack>
                </div >
                :
                (<div
                    key={data.title}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: "38px",
                        marginTop: "10px"
                    }}
                >
                    <span style={{
                        fontStyle: "normal",
                        fontWeight: 500,
                        fontSize: "13px",
                        color: "#0009",
                    }}
                    >{t("locationSet.filterModal." + data.title)}</span>
                    <span
                        style={{
                            fontStyle: "normal",
                            fontWeight: 400,
                            fontSize: "13px",
                            color: "#0009",
                        }}

                    >{t("locationSet.filterModal.notValue")}</span>
                </div>)
        } else {
            return <FormControl fullWidth key={data.title}>
                <InputLabel
                    color="success"
                >
                    {t("locationSet.filterModal." + data.title)}
                </InputLabel>
                <Select
                    name={data.title}
                    value={data.value}
                    onChange={(event) => handleChange(event, index)}
                    label={t("locationSet.filterModal." + data.title)}
                    color="success"
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 350,
                            },
                        },
                    }}
                >
                    {data.options.map((optionsItm: any) => <MenuItem value={optionsItm} key={optionsItm}>{t("locationSet.filterModal." + optionsItm)}</MenuItem>)}
                    <MenuItem value="allLocations" >{t("locationSet.filterModal.allLocations")}</MenuItem>
                </Select>
            </FormControl>
        }
    }

    useEffect(() => {
        let propsData = JSON.parse(JSON.stringify(props.data));
        propsData[1].children = propsData[1].children.map((item: any) => {
            if (item.type === "time") {
                if (item.min) item.min = dayjs(item.min)
                if (item.max) item.max = dayjs(item.max)
            }
            return item
        })
        setFilterData(propsData)
    }, [props.data])

    return filterData && (<Menu
        anchorEl={props.anchorEl}
        open={open}
        onClose={props.handleClose}
        sx={{
            "& .MuiList-root": {
                py: 0,
            },
        }}
    >
        <div className={styles.filterBox}>
            <div className={styles.filterHead}>{t("locationSet.filter")}</div>
            <div className={styles.filterContent}>
                {props.isActiveData ?
                    (() => {
                        let index = props.isActiveData.index;
                        let childIdx = props.isActiveData.childIdx;
                        let data = childIdx === undefined ? filterData[index] : filterData[index].children[childIdx];
                        return childrenEl(data, index, childIdx)
                    })()
                    :
                    filterData.map((item: any, index: number) => {
                        if (item.children) {
                            return <Accordion expanded={item.expanded} key={item.title}>
                                <AccordionSummary
                                    expandIcon={
                                        <IconButton
                                            component="span"
                                            size="small"
                                            onClick={() => {
                                                item.expanded = !item.expanded;
                                                setFilterData([...filterData])
                                                props.handleChange({ key: "expanded", value: item.expanded, index });
                                            }}
                                        >
                                            <div className="material-icons">keyboard_arrow_right</div>
                                        </IconButton>
                                    }
                                >
                                    {t("locationSet.filterModal." + item.title)}
                                </AccordionSummary>
                                <AccordionDetails>
                                    {item.children.map((childrenIte: any, i: number) => {
                                        if (!item.showMore || childrenIte.isTop || (item.showMore === "2")) {
                                            return childrenEl(childrenIte, index, i)
                                        }
                                    })}
                                    {item.showMore && <div className={styles.moreBtn}>
                                        <Button
                                            color="success"
                                            style={{ marginRight: '10px' }}
                                            onClick={() => {
                                                item.showMore = item.showMore === "1" ? "2" : "1";
                                                setFilterData([...filterData])
                                                props.handleChange({ key: "showMore", value: item.showMore, index });
                                            }}>
                                            {t(`buttons.${item.showMore === "1" ? "showMore" : "showLess"}`)}
                                        </Button>
                                    </div>}
                                </AccordionDetails>
                            </Accordion>
                        } else {
                            return childrenEl(item, index)
                        }

                    })
                }

            </div>
            <div className={styles.filterFoot}>
                <Button
                    color="success"
                    disabled={!btnDisabled}
                    onClick={handleReset}
                >
                    {t("buttons.reset")}
                </Button>
                <div>
                    <Button
                        color="success"
                        style={{ marginRight: '10px' }}
                        onClick={props.handleClose}>
                        {t("buttons.close")}
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={sameData(filterData, props.data)}
                        onClick={() => {
                            let arr = [...filterData];
                            arr[1].children = arr[1].children.map((item: any) => {
                                if (item.type === "time") {
                                    if (item.min) item.min = item.min.valueOf()
                                    if (item.max) item.max = item.max.valueOf()
                                }
                                return item
                            })
                            props.handleApply(arr)
                            props.handleClose()
                        }}
                    >
                        {t("buttons.apply")}
                    </Button>
                </div>
            </div>
        </div>
    </Menu >);
}
export default FilterLocations;